import React, { FC } from 'react';

import { useRouter } from 'next/router';

import { Text, HTMLText, BlockText } from '@morressier/ts';

import uniqBy from 'lodash/uniqBy';
import Dotdotdot from 'react-dotdotdot';
import { FaPlay, FaRegPlayCircle, FaVideoSlash } from 'react-icons/fa';

import { getPlaybackId } from 'components/MuxThumbnail';
import { SvgIcon } from 'components/Utilities/SvgIcon';
import { canDisplayVideoCutterPlayer } from 'components/VideoPlayer/helper';
import { LiveSvg } from 'icons/liveSvg';
import { VideoSession } from 'pages/event/[eventId]/sessions/[sessionId]';

import * as Styles from './styles';

export const getSpeakersFromPoster = (poster?: SessionPoster) => {
  const presenters = poster?.presentations?.presenters || [];
  const authors = poster?.authors || [];

  return uniqBy([...presenters, ...authors], 'id');
};

const PlaylistItemPreview: FC<{
  videoAvailable: boolean;
  playbackId: string | null;
  playback_url?: string;
  showThumbnail?: boolean;
}> = props => {
  const { videoAvailable } = props;
  // const thumbnail = useMuxThumbnail(playbackId);

  return (
    <Styles.PlaylistItemPreview>
      {videoAvailable ? <FaRegPlayCircle /> : <FaVideoSlash />}
      {/* {showThumbnail && thumbnail && <img src={thumbnail} alt="" />} */}
    </Styles.PlaylistItemPreview>
  );
};

export const Playlist: FC<{
  session: MorressierSession;
  playing: number | null;
  blocked: boolean;
  videoSessions: VideoSession;
  posters: (SessionPoster & { hasEnded: boolean })[];
  isHappeningNow: boolean;
  hasRecordings: boolean;
  liveStatus: MorressierSession['main_stage']['live_status'];
  shouldShowVideoModeForRtmp: boolean;
  totalCount: number;
}> = props => {
  const {
    session,
    posters,
    isHappeningNow,
    hasRecordings,
    liveStatus,
    shouldShowVideoModeForRtmp,
    totalCount,
  } = props;
  const showVideoPreview =
    session.mode === 'Video' ||
    (session.mode === 'Live' && hasRecordings) ||
    shouldShowVideoModeForRtmp;

  const { push, pathname } = useRouter();

  return (
    <Styles.PlaylistStyle color="white" length={posters.length} blocked={props.blocked}>
      <Styles.PlaylistHeader>
        <Text color="secondaryBrand" fontWeight="bold">
          Presentations (
          {props.playing !== null ? `${props.playing + 1}/${totalCount}` : totalCount})
        </Text>
      </Styles.PlaylistHeader>
      <Styles.PlaylistBody>
        {posters.map((poster, i) => {
          const presenter = getSpeakersFromPoster(poster).length
            ? getSpeakersFromPoster(poster)[0]
            : null;

          const videoAvailable =
            canDisplayVideoCutterPlayer(props.videoSessions, poster.id) ||
            poster.video_presentation?.playback_url;
          const isActive = i === props.playing;

          const playbackID = getPlaybackId(poster.video_presentation?.playback_url);
          const playback_url = poster.video_presentation?.playback_url;

          return (
            <Styles.PlaylistItem
              key={poster.id}
              showVideoPreview={showVideoPreview}
              onClick={() => {
                if (props.blocked) return;
                // we always want to make sure the route is updated
                // Router.push(`/session/${session._id}?poster-id=${poster.id}`);
                push(
                  {
                    pathname,
                    query: {
                      eventId: session.event.id,
                      sessionId: session._id,
                      'poster-id': poster.id,
                    },
                  },
                  undefined,
                  { shallow: true },
                );
              }}
              active={isActive}
              style={{ paddingLeft: 0 }}
            >
              <Styles.PlaylistItemNumber>
                {isActive ? (
                  <FaPlay style={{ fontSize: '0.5rem' }} />
                ) : (
                  <Text fontWeight="semiBold" size="caption_new">
                    {i + 1}
                  </Text>
                )}
              </Styles.PlaylistItemNumber>
              {showVideoPreview && (
                <PlaylistItemPreview
                  videoAvailable={!!videoAvailable}
                  playbackId={playbackID}
                  playback_url={playback_url}
                  showThumbnail={!props.blocked && hasRecordings}
                />
              )}
              <Styles.FillAvailable
                showVideoPreview={showVideoPreview}
                style={{
                  alignSelf: 'flex-start',
                }}
              >
                <BlockText
                  color={poster.hasEnded ? 'grey' : 'secondaryBrand'}
                  size="caption_new"
                  fontWeight="semiBold"
                  as="h4"
                  title={poster.title}
                >
                  {isActive &&
                    session.mode === 'Live' &&
                    (isHappeningNow || liveStatus === 'LIVE') && (
                      <SvgIcon color="lightRed" size="1rem">
                        <LiveSvg style={{ marginRight: 4 }} />
                      </SvgIcon>
                    )}
                  <Dotdotdot clamp={2}>
                    <HTMLText>{poster.title}</HTMLText>
                  </Dotdotdot>
                </BlockText>

                <Text
                  color="grey"
                  size="caption_new"
                  as="span"
                  style={{
                    marginTop: '2px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {presenter?.full_name}
                </Text>
              </Styles.FillAvailable>
            </Styles.PlaylistItem>
          );
        })}
      </Styles.PlaylistBody>
    </Styles.PlaylistStyle>
  );
};
